import React from 'react';
import GetStartedSection from '../components/GetStartedSection';
import Navbar from '../components/Navbar';
import StatsSection from '../components/StatsSection';
import FeaturesSection from '../components/funnel35/FeaturesSection';
import StoryMarquee from '../components/StoryMarquee';
import { SUCCESS_STORIES } from '../components/funnel35/landing/landingContent';
import useFunnelNavigation from '../hooks/useFunnelNavigation';
import { useTrackingCapture } from '../hooks/useTrackingCapture';
import TestimonialsSection from '../components/TestimonialsSection';
import AchievementsSection from '../components/AchievementsSection';
import JobSearchResults from '../components/JobSearchResults';
import HeroSectionSlider from '../components/HeroSectionSlider';
import useMobile from '../helpers/useMobile';
import { getBrand } from '../utils/getBrand';
import { RA_PAGE_BG, RA_PAGE_BG_REVERSE } from '../components/funnel35/theme';
import ericaImg from '../assets/erica.png';
import haileyImg from '../assets/hailey.png';
import jaylenImg from '../assets/jaylen.png';
import donaldImg from '../assets/donald.png';
import camilaImg from '../assets/camila.png';
import monicaImg from '../assets/monica.png';
import chenImage from '../assets/chen.png';
import calendarIcon from '../assets/calendar.svg';
import stopwatchIcon from '../assets/stopwatch.svg';
import diagramIcon from '../assets/diagram.svg';
import thumbsUpIcon from '../assets/thumbs_up.svg';

/**
 * RemoteApply homepage: the BetterApply homepage (pages/Home.jsx) with the
 * green/purple brand surfaces swapped for the RemoteApply blue palette.
 * The hero photo strip and the features grid are the funnel 35 landing /
 * checkout ones so the homepage matches the funnel. CTAs resolve to funnel 35
 * through brandDefaultFunnelNr.
 */
const RemoteApplyHome = () => {
  const { navigateToQuiz, navigateToCheckout } = useFunnelNavigation();
  const { brandName } = getBrand();
  const isMobile = useMobile(478);
  // Capture tracking data on home page load
  useTrackingCapture();

  // Same success-story strip as the funnel 35 landing hero: horizontal row on
  // mobile, two staggered vertical columns on desktop.
  const heroMedia = (
    <>
      <StoryMarquee
        stories={SUCCESS_STORIES}
        mobileBreakpoint={100000}
        className="block md:hidden h-[300px] flex items-center overflow-hidden"
      />
      <StoryMarquee
        stories={SUCCESS_STORIES}
        mobileBreakpoint={0}
        height="600px"
        className="hidden md:block overflow-hidden rounded-3xl"
      />
    </>
  );

  const testimonials = [
    {
      name: 'Erica T., 35',
      role: 'Accountant',
      text: 'Honestly, it felt like having a personal recruiter. I went from zero responses to multiple interviews in just a few weeks and ended up with several options. Wish I’d had this sooner!',
      verified: true,
      image: ericaImg,
    },
    {
      name: 'Hailey M., 32',
      role: 'Social media manager',
      text: 'I used to spend over 30 hours a week applying manually and hearing nothing. Once I switched to this, interviews started coming in almost immediately. The personalized resumes made a huge difference.',
      verified: true,
      image: haileyImg,
    },
    {
      name: 'Jaylen R., 27',
      role: 'Software engineer',
      text: 'This is nuts. I used to spend hours applying to like five jobs. Then suddenly, I was sending out hundreds automatically, getting interviews nonstop, and companies were literally competing for me. What??',
      verified: true,
      image: jaylenImg,
    },
    {
      name: 'Donald H., 47',
      role: 'Copywriter',
      text: `I had no idea how many great jobs I was missing until I tried ${brandName}. It found openings I would’ve never come across on my own—and one of them turned out to be the perfect fit. I’m so happy! I love my new job!`,
      verified: true,
      image: donaldImg,
    },
    {
      name: 'Camila H., 24',
      role: 'Data analyst',
      text: 'I always thought job hunting had to be stressful, but this changed my mind. Everything from applying to prep felt smooth! The interview coaching was my favorite—it felt like having insider info.',
      verified: true,
      image: camilaImg,
    },
    {
      name: 'Monica J., 43',
      role: 'Account manager',
      text: 'I always had a feeling, but I never realized just how underpaid I was at my last job. This helped me prove my value, ace interviews, and walk away with a far better deal. A total lifesaver.',
      verified: true,
      image: monicaImg,
    },
  ];

  return (
    <>
      <Navbar showNavigation={false} showSignIn={true} showGetStarted={true} />
      <main id="main-content">
        <HeroSectionSlider
          content={{
            avatarText: isMobile ? (
              <div className="text-[14px]/[22px]">
                <span className="font-semibold">10,500+</span> people found jobs last month
              </div>
            ) : (
              <>
                <span className="font-semibold">10,500+</span> people found new jobs last month
              </>
            ),
            textOne: 'Get hired 3x faster. Hassle-free.',
            textTwo: null,
            textThree: 'Don’t wait—land your dream job and salary with zero stress:',
          }}
          media={heroMedia}
          modifiers={{
            textThreeStyles: { color: 'black' },
            textOneStyle: { fontSmall: true },
          }}
          displayChecklist={true}
          checklistContent={[
            'Perfectly matched jobs you’re most likely to land',
            'Resumes and cover letters tailored to each role',
            'Jobs auto-applied for you',
            'Interviews aced with a live AI coach',
          ]}
          firstButtonText="Get my job matches now"
          showSecondButton={false}
        />
        <StatsSection
          stats={[
            { value: '150k+', label: 'Users found their dream job' },
            { value: '30 days', label: 'Average time to first job offer' },
            { value: '+32%', label: 'Average pay jump' },
            { value: '40h+', label: 'Saved every month' },
          ]}
          color="purple"
          title="Our users are living proof that it works"
          modifiers={{ background: RA_PAGE_BG }}
        />
        <FeaturesSection
          firstButtonText="Get my job matches now"
          secondButtonText={`Get started with ${brandName}`}
          onFirstButtonClick={() => navigateToQuiz(1)}
          onSecondButtonClick={() => navigateToCheckout()}
        />
        <TestimonialsSection
          showFirstButton={true}
          showSecondButton={true}
          firstButtonText="Get my job matches now"
          secondButtonText={`Get started with ${brandName}`}
          onFirstButtonClick={() => navigateToQuiz(1)}
          onSecondButtonClick={() => navigateToCheckout()}
          testimonials={testimonials}
          modifiers={{
            showImageBg: false,
            sectionBg: 'bg-gradient-to-b from-[#F5F8FF] to-[#DCE6FF]',
            shadowLeft: 'linear-gradient(to right, rgba(37, 99, 235, 0.18), transparent)',
            shadowRight: 'linear-gradient(to left, rgba(37, 99, 235, 0.18), transparent)',
          }}
        />
        <AchievementsSection />
        <JobSearchResults
          content={{
            title: 'What happens when job search is done right',
            description: 'Faster offers, bigger salaries, and happier jobs — here’s what users are getting.',
            numbersTitle: 'By the Numbers',
            numbers: [
              { title: 'Time to a new job', value: '27 days', icon: calendarIcon },
              { title: 'Total time invested', value: '5 hours', icon: stopwatchIcon },
              { title: 'Salary increase from last job', value: '32%', icon: diagramIcon },
              { title: 'Satisfaction rate', value: '95%', icon: thumbsUpIcon },
            ],
            testimonial: {
              name: 'Emily Chen',
              role: 'Data scientist',
              text: 'You guys said it could happen fast, but that fast? In less than a month, I had multiple offers from tech companies—and I’d barely put in any time. They were all great, but I’d always dreamed of working at Netflix, so I gladly accepted. Thank you!',
              rating: '4.5 out of 5 stars',
              image: chenImage,
              showSalaryIncrease: false,
            },
            modifiers: { testimonial: { background: 'bg-[#EEF3FF]' } },
          }}
        />
        <GetStartedSection
          showGetStarted={true}
          modifiers={{ background: RA_PAGE_BG_REVERSE, textStyle: 'text-[#16202E]', iconColor: '#16202E' }}
        />
      </main>
    </>
  );
};

export default RemoteApplyHome;
